/**
 * O tokenizador — de texto para lista de símbolos.
 *
 * Etapa 1 de três (`tokenizer` → `parser` → `eval`), e a única que olha caractere a caractere.
 *
 * ## Duas decisões que não se adivinham
 *
 * **1. O separador decimal aqui é o PONTO, sempre — e isso não contradiz o pt-BR.**
 * A vírgula é o separador de ARGUMENTO (`round(x, 2)`, `if(c, a, b)`). Aceitar `1,5` como
 * número tornaria `min(1,5)` ambíguo entre *"o mínimo de 1 e 5"* e *"o número 1,5 sozinho"* —
 * e a ambiguidade não se resolve com mais regra, só com outra escolha.
 * Onde o pt-BR importa é na **entrada e na saída do usuário** (`formato.ts`), não na sintaxe
 * da expressão. É a mesma separação que toda linguagem faz.
 *
 * **2. `prop("Valor")` guarda o NOME no token, e a resolução para id acontece depois.**
 * O tokenizador não conhece colunas. Quem traduz nome → `propertyId` é o parser, que recebe o
 * catálogo. Misturar as duas coisas aqui faria o tokenizador precisar do `SingleView`, e o
 * avaliador deixaria de ser puro — que é a propriedade (D-D do Discovery) que permite testá-lo
 * sem navegador.
 */

import { erro } from './erros';

export type TipoDeToken =
  | 'numero'
  | 'texto'
  | 'identificador'
  | 'operador'
  | 'abre'
  | 'fecha'
  | 'virgula'
  | 'fim';

export interface Token {
  tipo: TipoDeToken;
  /** Para `numero`, o valor já convertido. Para o resto, o texto do lexema. */
  valor: string | number;
  posicao: number;
}

/** Operadores, do mais LONGO para o mais curto — senão `>=` seria lido como `>` e depois `=`. */
const OPERADORES = ['>=', '<=', '==', '!=', '>', '<', '+', '-', '*', '/'] as const;

const ehDigito = (c: string) => c >= '0' && c <= '9';
const ehInicioDeIdentificador = (c: string) =>
  (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_';
const ehIdentificador = (c: string) => ehInicioDeIdentificador(c) || ehDigito(c);

export function tokenizar(entrada: string): Token[] {
  const texto = String(entrada ?? '');
  const tokens: Token[] = [];
  let i = 0;

  while (i < texto.length) {
    const c = texto[i];

    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i += 1;
      continue;
    }

    if (c === '(') { tokens.push({ tipo: 'abre', valor: '(', posicao: i }); i += 1; continue; }
    if (c === ')') { tokens.push({ tipo: 'fecha', valor: ')', posicao: i }); i += 1; continue; }
    if (c === ',') { tokens.push({ tipo: 'virgula', valor: ',', posicao: i }); i += 1; continue; }

    // Texto entre aspas. As duas formas são aceitas porque o usuário digita as duas, e
    // recusar uma delas seria rigor sem propósito.
    if (c === '"' || c === "'") {
      const abertura = c;
      const inicio = i;
      i += 1;
      let acumulado = '';
      let fechou = false;
      while (i < texto.length) {
        if (texto[i] === '\\' && i + 1 < texto.length) {
          // Escape mínimo: só o necessário para escrever a própria aspa e a barra.
          const seguinte = texto[i + 1];
          acumulado += seguinte === 'n' ? '\n' : seguinte;
          i += 2;
          continue;
        }
        if (texto[i] === abertura) { fechou = true; i += 1; break; }
        acumulado += texto[i];
        i += 1;
      }
      if (!fechou) {
        throw erro('sintaxe', `texto sem aspa de fechamento a partir da posição ${inicio}`, inicio);
      }
      tokens.push({ tipo: 'texto', valor: acumulado, posicao: inicio });
      continue;
    }

    if (ehDigito(c) || (c === '.' && ehDigito(texto[i + 1] ?? ''))) {
      const inicio = i;
      while (i < texto.length && ehDigito(texto[i])) i += 1;
      if (texto[i] === '.') {
        i += 1;
        while (i < texto.length && ehDigito(texto[i])) i += 1;
      }
      const lexema = texto.slice(inicio, i);
      const valor = Number(lexema);
      if (!Number.isFinite(valor)) {
        throw erro('sintaxe', `número inválido: "${lexema}"`, inicio);
      }
      tokens.push({ tipo: 'numero', valor, posicao: inicio });
      continue;
    }

    if (ehInicioDeIdentificador(c)) {
      const inicio = i;
      while (i < texto.length && ehIdentificador(texto[i])) i += 1;
      tokens.push({ tipo: 'identificador', valor: texto.slice(inicio, i), posicao: inicio });
      continue;
    }

    const operador = OPERADORES.find(op => texto.startsWith(op, i));
    if (operador) {
      tokens.push({ tipo: 'operador', valor: operador, posicao: i });
      i += operador.length;
      continue;
    }

    // ⚠️ `=` sozinho cai aqui, e a mensagem diz o que fazer. Quem vem de planilha escreve
    // `a = b` esperando comparação; devolver "caractere inesperado" mandaria procurar no
    // lugar errado.
    if (c === '=') {
      throw erro('sintaxe', 'use "==" para comparar (o "=" sozinho não é operador aqui)', i);
    }

    throw erro('sintaxe', `caractere inesperado: "${c}"`, i);
  }

  tokens.push({ tipo: 'fim', valor: '', posicao: texto.length });
  return tokens;
}
